import type { IconName } from './utils/icons';
import type { FeatureFlagKey } from './hooks/useFeatureFlags';
import type {
  Auth0Config,
  EntityBase,
  PspUserInfo,
  AppRegistryCallbackContext,
  ModuleUrlResolver,
} from '@opengov/psp-contract';
import { HostingEnvironment } from '@opengov/psp-contract';

// ============================================================================
// Dynamic Module Loading Types (PSP-internal)
// ============================================================================

/**
 * Context object passed to module URL resolvers.
 * Contains runtime information needed to determine the correct module URL.
 *
 * Note: AppRegistryCallbackContext (exported from @opengov/psp-contract) is
 * derived from this interface but without appRegistry and routeRegistry.
 */
export interface PspAppContext {
  hostingEnvironment: HostingEnvironment;
  appRegistry: readonly AppConfig[];
  routeRegistry: readonly ResolvedRouteRegistryEntry[];
  auth0Config: Auth0Config;
  activeEntity: EntityBase | undefined;
  allowDevToolOverrides?: boolean;
  user?: PspUserInfo;
}

/**
 * PSP-internal extension of {@link AppRegistryCallbackContext} used when
 * resolving `moduleConfig` callbacks. Carries entitlement product keys derived
 * from EntityContext so registry code can pass derived flags (e.g.
 * `hasInventoryEntitlement`) via `registryConfig` without expanding the
 * public @opengov/psp-contract surface.
 */
export interface PspModuleConfigContext extends AppRegistryCallbackContext {
  entitledProductKeys?: readonly string[];
  entityEntitledProductKeys?: readonly string[];
}

/**
 * Defines a route pattern and its corresponding module URL resolver.
 * Uses path-to-regexp syntax for pattern matching (e.g., '/', '/:activeEntity/reporting', '/admin/*').
 */
export interface ModuleRoute {
  pattern: string;
  resolver: ModuleUrlResolver;
}

// ============================================================================
// Route Registry Types
// ============================================================================

/**
 * Route registry entry IDs.
 * Use this enum when referencing routes to avoid hardcoded strings getting out of sync.
 */
export enum RouteRegistryId {
  PROFILE_SETTINGS = 'profile-settings',
  ACCESS_MANAGEMENT = 'access-management',
  FORMS = 'forms',
  DATA_MANAGER = 'data_manager',
  ORG_REGISTRY = 'org-registry',
  ONBOARDING = 'onboarding',
  NOTIFICATIONS_SETTINGS = 'notifications-settings',
  GAB_APPS = 'gab-apps',
}

/**
 * Route registry entry with callback functions.
 * For routes that load ES modules or local components but don't appear in navigation.
 */
export interface RouteRegistryEntry {
  id: RouteRegistryId | string;
  basePath: string;
  description?: string;
  // Either moduleRoutes (for remote ES modules) or component (for local React components)
  moduleRoutes?: readonly ModuleRoute[];
  moduleConfig?: (
    ctx: PspModuleConfigContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any>;
  /**
   * Callback that returns whether navigating away from this route should
   * trigger a full page refresh. Receives the resolution context (including
   * `featureFlags`), so LD-gated routes can read the flag directly:
   *   `(ctx) => !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH]`.
   * Use a constant-returning callback (`() => true` / `() => false`) for
   * unconditional behavior. Omit to opt out entirely.
   */
  triggerPageRefreshOnNavigateAway?: (ctx: AppRegistryCallbackContext) => boolean;
  /** Route patterns accessible without authentication. Same shape as `moduleRoutes`. */
  unauthenticatedRoutes?: readonly ModuleRoute[];
  /** When true, RemoteAppShell renders as a fixed-position full-viewport overlay. */
  fullPage?: boolean;
  /**
   * LaunchDarkly feature flag key that gates this route. If set, the route only exists while
   * the flag is truthy. If omitted, no flag gate is applied.
   */
  featureFlagKey?: FeatureFlagKey;
}

/**
 * Route registry entry after callbacks have been executed.
 */
export interface ResolvedRouteRegistryEntry {
  id: RouteRegistryId | string;
  basePath: string;
  description?: string;
  moduleRoutes?: readonly (Omit<ModuleRoute, 'resolver'> & { resolver: string })[];
  moduleConfig?: (
    ctx: PspModuleConfigContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any>;
  /**
   * Resolved value of the source entry's `triggerPageRefreshOnNavigateAway`
   * callback (populated by `PspContext`). When `true`, the route triggers a
   * full page refresh on navigate-away.
   */
  triggerPageRefreshOnNavigateAway?: boolean;
  unauthenticatedRoutes?: readonly (Omit<ModuleRoute, 'resolver'> & { resolver: string })[];
  /** When true, RemoteAppShell renders as a fixed-position full-viewport overlay. */
  fullPage?: boolean;
}

/**
 * Global registry entry — loads at PSP startup and stays mounted for the
 * full app lifetime, regardless of which route-based app is active.
 */
export interface UnresolvedGlobalRegistryEntry {
  id: string;
  /** Resolver returning the module URL for the current environment */
  moduleUrl: ModuleUrlResolver;
  /** Optional config factory — same shape as RouteRegistryEntry.moduleConfig */
  moduleConfig?: (
    ctx: PspModuleConfigContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
  /**
   * LaunchDarkly feature flag key that gates this entry.
   * If set, the entry only loads when the flag is truthy.
   * If omitted, no flag gate is applied.
   */
  featureFlagKey?: FeatureFlagKey;
}

/** Resolved form of a global registry entry (URLs are strings, not functions) */
export type GlobalRegistryEntry = Omit<
  UnresolvedGlobalRegistryEntry,
  'moduleUrl' | 'moduleConfig'
> & {
  moduleUrl: string;
  moduleConfig?: Record<string, unknown>;
};

/**
 * Shared interface for components that can render remote ES modules.
 * Used by RemoteAppShell to accept both app registry and route registry entries.
 */
export interface RemoteModuleConfig {
  id: string;
  basePath: string;
  moduleRoutes: readonly (Omit<ModuleRoute, 'resolver'> & { resolver: string })[];
  /** Resolved lazily once per (app, entity) per session. Must not depend on token freshness. */
  moduleConfig?: (
    ctx: PspModuleConfigContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
}

// ============================================================================
// App Types
// ============================================================================

export enum MenuSectionTitle {
  ACTION_HUBS = 'Your Action Hub',
  TEST_APPS = 'Local Test Apps',
  CAPABILITIES = 'Capabilities',
  PRODUCTS = 'Products',
  PREFERENCES = 'Preferences',
  DEVELOPER_TOOLS = 'Developer Tools',
}

export enum MenuGroupName {
  FINANCIALS = 'Financials',
  HUMAN_CAPITAL_MANAGEMENT = 'Human Capital Management',
}

/**
 * Maps menu group names to their corresponding icon names.
 * Keys must be from MenuGroupName enum, values must be valid IconName values.
 */
export type MenuGroupIcon = {
  [K in MenuGroupName]: IconName;
};

export enum EntityApplicationProductKey {
  ADMIN_SETTINGS = 'adminsettings',
  UTILITY_BILLING = 'ub',
  ACCOUNTS_RECEIVABLE = 'ar',
  CASH_RECEIPTS = 'cr',
  BUDGET = 'budget',
  PERFORMANCE_MANAGEMENT = 'perfmgmt',
  REPORTING = 'reporting',
  AGENT_STUDIO = 'agentstudio',
  APP_BUILDER = 'gab',
  COMMUNITY_FEEDBACK = 'community-engagement',
  DEVELOPER_PORTAL = 'dp',
  TAX_REVENUE = 'tnr',
  FINANCIALS = 'financial',
  PROCUREMENT = 'procurement',
  VENDOR_MANAGEMENT = 'vm',
  CLM = 'clm',
  PERMITTING_LICENSING = 'cit',
  ASSET_MANAGEMENT = 'eam',
  OD = 'od',
  DATA_MANAGER = 'data_manager',
  WORKFLOW_BUILDER = 'wfb',
  HCM = 'hcm',
  PAYROLL = 'payroll',
  WORKFORCE_PLANNING = 'wfp',
  LMS = 'lms',
  FIXED_ASSETS = 'fixed-assets',
  INVENTORY = 'inventory',
  ATLAS = 'atlas',
  PCARD = 'pcard',
  SITES = 'sites',
  PRODUCT_311 = 'product311',
  GRANTS_MANAGEMENT = 'grantsmgmt',
  OG360 = 'og360',
}

/**
 * Dynamic GAB entitlement key: `j` for v2 or `m` for v1, plus the app's key.
 * See `isGabDynamicEntitlementKey` in `services/gabDynamicAppConfigs.ts` for the exact shape.
 */
export type GabDynamicEntitlementKey = `j${string}` | `m${string}`;

/** A key an entitlement can carry: a static product's, or a gab-core app's created at runtime. */
export type EntitlementProductKey = EntityApplicationProductKey | GabDynamicEntitlementKey;

export enum AppCategory {
  STATIC = 'static', // Always shown (Action Hubs, Preferences, etc.)
  TEST = 'test', // Dev-only test applications
  ENTITLED = 'entitled', // Requires entitlement check to show
}

// Base AppConfig with common fields
interface BaseAppConfig {
  id: EntityApplicationProductKey | string;
  category: AppCategory;
  // basePath is the base path for the app relative to the PSP shell root path
  // e.g.
  // root = psp.domain.com
  // basePath = "/base-path"
  // full app path = psp.domain.com/base-path
  basePath: string;
  displayName: string;
  icon: IconName;
  description?: string;
  menuSection: MenuSectionTitle;
  // Support for iframe and new tab apps
  externalUrl?: (pspContext: AppRegistryCallbackContext) => string | Promise<string>;
  openInNewTab?: boolean;
  // Optional group for organizing related apps under a parent menu item
  group?: MenuGroupName;
  /**
   * Required. Callback that returns whether navigating away from this app
   * should trigger a full page refresh. Receives the resolution context
   * (including `featureFlags`), so LD-gated apps can read the flag directly:
   *   `(ctx) => !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH]`.
   * Use a constant-returning callback (`() => true` / `() => false`) for
   * unconditional behavior. Every registry entry must declare this — the
   * intent is to force every new app to make a deliberate choice about
   * cross-app navigation refresh rather than inheriting an implicit default.
   *
   * Useful for apps that leave persistent DOM artifacts, event listeners, or
   * other state that wouldn't be cleared by client-side navigation alone.
   */
  triggerPageRefreshOnNavigateAway: (ctx: AppRegistryCallbackContext) => boolean;
  /**
   * Resource subject patterns that this app handles.
   *
   * **Primary Use**: Task deep linking - enables matching task subjects to rewrite
   * absolute deep link URLs into PSP-friendly internal routes for seamless navigation
   * within the PSP shell.
   *
   * **Generic Purpose**: Matches resource subjects (tasks, notifications, etc.) to their
   * corresponding apps based on subject identifiers. The resolver returns the full AppConfig,
   * enabling extraction of basePath, displayName, icon, or any other app metadata.
   *
   * **Format**: URN format `urn:opengov:{resource}:{segments}*`
   * - resource: Primary identifier (e.g., "financial", "procurement", "vm")
   * - segments: Optional additional segments (more specific patterns take precedence)
   * - Over-specified subjects are matched by prefix (extra segments ignored)
   *
   * @example
   * // Simple resource match
   * resourceSubjectPatterns: ["urn:opengov:procurement"]
   * // Matches: urn:opengov:procurement:po, urn:opengov:procurement:contracts, etc.
   *
   * // Multiple patterns (app handles multiple resource types)
   * resourceSubjectPatterns: ["urn:opengov:financial", "urn:opengov:erp"]
   *
   * // Specific identifier (higher specificity score)
   * resourceSubjectPatterns: ["urn:opengov:financial:general-ledger"]
   * // Matches: urn:opengov:financial:general-ledger (and any extra segments)
   */
  resourceSubjectPatterns?: readonly string[];
  /**
   * LaunchDarkly feature flag key that gates this app entry.
   * If set, the app only appears in the registry when the flag is truthy.
   * If omitted, no flag gate is applied.
   */
  featureFlagKey?: FeatureFlagKey;
  /**
   * Additional entitlement product keys required beyond the primary `id`.
   * When set, the app is only shown if the entity has ALL listed entitlements
   * in addition to the primary `id` entitlement.
   * When omitted, only the `id` entitlement is checked (backward compatible).
   */
  requiredEntitlements?: readonly EntityApplicationProductKey[];
  /**
   * When true, the entry is a navigation item only: it appears in the sidebar and on All
   * Products, but `App.tsx` gives it no route, because a route registry entry serves its path.
   * gab-core apps use this: each has its own sidebar item, and the one `/a` mount in the route
   * registry serves all of them. A route per entry would outrank that mount.
   */
  navigationOnly?: boolean;
  /**
   * PLATFORM claim key prefixes that gate this app in the navigation.
   * When set, the app is only shown if the user holds at least one claim at
   * the active entity whose key starts with one of these prefixes
   * (case-insensitive), in addition to any category/entitlement check.
   *
   * @example
   * // Shown to anyone with a scoped audit READ or READ_PII claim, e.g. PLATFORM_AUDIT__READ__PAYROLL
   * requiredClaimPrefixes: ['PLATFORM_AUDIT__READ__', 'PLATFORM_AUDIT__READ_PII__']
   */
  requiredClaimPrefixes?: readonly string[];
}

// AppConfig with moduleRoutes (for remote applications with dynamic module loading)
export interface RemoteAppConfig extends BaseAppConfig {
  // moduleConfig can be sync or async - modules should handle both cases (await if Promise)
  moduleConfig?: (
    pspContext: PspModuleConfigContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
  moduleRoutes?: readonly ModuleRoute[];
  /**
   * Route patterns accessible without authentication.
   * Uses same `{pattern, resolver}` shape as `moduleRoutes`.
   * Patterns are relative to the app's `basePath` and use `path-to-regexp` syntax.
   *
   * Unauthenticated routes bypass the auth gate entirely — remote modules on these
   * routes receive `activeEntity: undefined` and no auth token. They must not depend
   * on authenticated user, entity, or feature-flag context.
   *
   * @example
   * unauthenticatedRoutes: [
   *   { pattern: '/registration/complete', resolver: (ctx) => `https://.../${ctx.hostingEnvironment}/setup.js` },
   * ]
   */
  unauthenticatedRoutes?: readonly ModuleRoute[];
  component?: never; // Cannot have both moduleRoutes and component
  // Legacy support: moduleUrl is deprecated but still supported for backward compatibility
  moduleUrl?: never;
}

// AppConfig with component (for local applications)
export interface LocalAppConfig extends BaseAppConfig {
  moduleRoutes?: never; // Cannot have both moduleRoutes and component
  moduleUrl?: never; // Cannot have both moduleUrl and component
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
}

// AppConfig with externalUrl (for iframe or new tab applications)
export interface ExternalUrlAppConfig extends Omit<BaseAppConfig, 'externalUrl'> {
  externalUrl: string | NonNullable<BaseAppConfig['externalUrl']>; // Required URL or resolver
  moduleRoutes?: never; // Cannot have both moduleRoutes and externalUrl
  component?: never; // Cannot have both component and externalUrl
  moduleUrl?: never;
}

// AppConfig is either a remote app (with moduleRoutes), local app (with component), or external URL app (with externalUrl)
export type UnresolvedAppConfig = RemoteAppConfig | LocalAppConfig | ExternalUrlAppConfig;

export type AppConfig = Omit<
  UnresolvedAppConfig,
  | 'externalUrl'
  | 'moduleConfig'
  | 'moduleRoutes'
  | 'unauthenticatedRoutes'
  | 'triggerPageRefreshOnNavigateAway'
> & {
  externalUrl?: string;
  moduleConfig?: (
    pspContext: PspModuleConfigContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
  moduleRoutes?: readonly (Omit<ModuleRoute, 'resolver'> & { resolver: string })[];
  unauthenticatedRoutes?: readonly (Omit<ModuleRoute, 'resolver'> & { resolver: string })[];
  triggerPageRefreshOnNavigateAway?: boolean;
};

/**
 * A resolved AppConfig that has moduleRoutes defined.
 * This represents a remote app configuration that has been resolved and is ready to use.
 */
export type ResolvedRemoteAppConfig = AppConfig & {
  moduleRoutes: NonNullable<AppConfig['moduleRoutes']>;
};

// Type variations for type-safe unresolved app configurations
// These maintain the discriminated union (either moduleUrl or component)
// Note: These are for the unresolved app registry. Once resolved, use AppConfig.
export type EntitledUnresolvedAppConfig = (RemoteAppConfig | LocalAppConfig) & {
  // Static entries must use a product key from the enum. gab-core app entries are built at
  // runtime from their entitlement and have their own type (`GabDynamicAppConfig`).
  id: EntityApplicationProductKey;
  category: AppCategory.ENTITLED;
};

export type TestUnresolvedAppConfig = (RemoteAppConfig | LocalAppConfig | ExternalUrlAppConfig) & {
  category: AppCategory.TEST;
};

// Static apps skip the entitlement check. Usually local components; a remote
// module is allowed too (e.g. Chronos Audit Logs, gated by requiredClaimPrefixes).
export type StaticUnresolvedAppConfig = (RemoteAppConfig | LocalAppConfig) & {
  category: AppCategory.STATIC;
};

// Legacy type aliases for backward compatibility during migration
// TODO: Remove these after full migration
export type EntitledAppConfig = EntitledUnresolvedAppConfig;
export type TestAppConfig = TestUnresolvedAppConfig;
export type StaticAppConfig = StaticUnresolvedAppConfig;

export interface EntitlementProduct {
  entitlementProductKey: EntityApplicationProductKey;
  url: string;
  description: string;
  displayName: string;
}

// ============================================================================
// Navigation Types
// ============================================================================

export interface MenuItem {
  id: string;
  label: string;
  icon: IconName;
  path: string;
  // Support for iframe and new tab apps
  externalUrl?: string;
  openInNewTab?: boolean; // Flag to indicate this menu item should open in a new tab using the "path" property URL
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any>;
  dotBadge?: boolean;
  isRemoteApp?: boolean; // Flag to indicate this menu item loads a remote application
  // Optional children for grouped menu items (parent items contain children)
  children?: MenuItem[];
  // Flag to indicate this is a group parent (non-clickable, only expands/collapses)
  isGroup?: boolean;
  isPinned?: boolean;
}

export interface MenuSection {
  id: string;
  title: MenuSectionTitle;
  collapsible: boolean;
  defaultExpanded?: boolean;
  items: MenuItem[];
}

export interface NavigationConfig {
  sections: MenuSection[];
}

// ============================================================================
// Entity Types
// ============================================================================

// EntityBase is defined in @opengov/psp-contract

// Core entity type returned from /api/access/user/v1/{user_uuid}/entities
export interface Entity extends EntityBase {
  primary?: boolean;
  entityType?: string;
  logo?: string;
  timezone?: string | null;
  address?: {
    street?: string;
    city?: string;
    region?: string;
    country?: string;
  };
  thirdPartyIds?: {
    salesforceId?: string;
  };
  entitledProducts?: EntityApplication[];
}

// Entitlement entry for a given entity/product
export interface EntityApplication {
  entitlementProductKey: EntitlementProductKey;
  url: string;
  description: string;
  displayName: string;
  openInNewTab?: boolean;
  // Optional icon mapping when rendering inside PSP (not from API)
  iconName?: IconName;
}

// Response for /entitlements?entityIds=...&idType=BY_FRIENDLY_ID
export interface EntityEntitlement {
  entityUuid: string;
  entitlements: EntityApplication[];
}

// ============================================================================
// Logger Types (PSP-internal)
// ============================================================================

/** Log levels supported by the logger */
export type LogLevel = 'log' | 'info' | 'warn' | 'error';

/** Log function options */
export interface LogOptions {
  /** If true, the provided context will be persisted and included in all future logs */
  persistContext?: boolean;
}

/** Log function signature */
export type LogFunction = (
  message: string,
  context?: Record<string, unknown>,
  options?: LogOptions
) => void;

/**
 * Logger service interface from psc-react-logger.
 * Provides methods for logging at different severity levels.
 */
export interface Logger {
  log: LogFunction;
  info: LogFunction;
  warn: LogFunction;
  error: LogFunction;
}

/**
 * Application metadata
 */
export interface AppMeta {
  /** Application name (e.g., 'public-service-platform') */
  name: string;
  /** Application version from package.json */
  version: string;
  /** Deployment environment */
  environment: 'development' | 'integration' | 'staging' | 'production';
}

/**
 * User context for logging
 * Includes username for log readability
 * MUST NOT contain email, phone, address, or other sensitive PII
 */
export interface UserContext extends Record<string, unknown> {
  /** Auth0 user ID (sub) or OpenGov platform_user.uuid */
  user_id: string;
  /** User display name for log readability */
  user_name?: string;
  /** OpenGov platform_user.id (if available) */
  user_platform_user_id?: string;
  /** User role (e.g., 'admin', 'viewer') */
  user_role?: string;
}

/**
 * Entity context from PSP EntityContext provider
 */
export interface EntityContext {
  /** Entity UUID */
  id: string;
  /** Short ID (e.g., 'sunnyvale') */
  friendlyId: string;
  /** Numeric entity ID */
  entityId: number;
  /** Entity name (e.g., 'City of Sunnyvale') */
  name: string;
}

/** Module loading context (PSP-internal telemetry) */
export interface ModuleContext {
  /** URL of the ES module being loaded */
  moduleUrl: string;
  /** App base path (e.g., '/agents', '/budget') */
  basePath: string;
  /** App identifier from appRegistry */
  appId: string;
  /** Loading result */
  outcome: 'success' | 'failure';
  /** Load duration in milliseconds (if measured) */
  duration?: number;
  /** Error message (if failure) */
  error?: string;
}

// ============================================================================
// URN Types
// ============================================================================

/**
 * Parsed resource subject identifier.
 *
 * Resource subjects identify which app should handle a resource (task, notification, etc.).
 * Currently uses URN format: `urn:opengov:{resource}:{segment}*`
 *
 * @example
 * parseResourceSubject("urn:opengov:procurement:po")
 * // Returns:
 * {
 *   scheme: 'urn',
 *   namespace: 'opengov',
 *   resource: 'procurement',
 *   segments: ['po'],
 *   original: 'urn:opengov:procurement:po'
 * }
 */
export interface ResourceSubject {
  /** URN scheme (always "urn") */
  scheme: 'urn';
  /** Namespace (always "opengov") */
  namespace: 'opengov';
  /** Primary resource identifier (e.g., "procurement", "financial", "vm") */
  resource: string;
  /** Additional path segments after resource */
  segments: string[];
  /** Original subject string */
  original: string;
}
